import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import { useLocation, useParams } from 'react-router-dom';
import { useSmartBack } from '../../shared/navigation';
import { useQuery, useMutation } from '@tanstack/react-query';
import GridWrapper from '../../shared/components/layout/GridWrapper';
import Avatar from 'react-avatar';
import TextareaAutosize from 'react-textarea-autosize';
import toast from 'react-hot-toast';
import {
  Building2,
  Download,
  Globe,
  Link2,
  Loader2,
  Lock,
  MessageSquare,
  Paperclip,
  Pencil,
  SendHorizontal,
  ThumbsUp,
  X
} from 'lucide-react';
import {
  getPostById,
  editPost,
  createComment,
  createReply,
  createLike,
  deleteLike,
  getComments,
  getReplies,
  getUsersByChar,
  setCommentLiked
} from '../services/api/implementation/impl';
import { uploadImage } from '../../shared/services/api/implementation/impl';
import Slider from 'react-slick';
import socket from '../../../socket'; // Import socket
import { TONE_SOFT } from '../../shared/components/ui/primitives/tone';
import {
  MentionMenu,
  TaggedPeople,
  VisibilityConfirm,
  TONE_EDGE,
  fullTimestamp,
  isPostAuthor,
  tagInfoFor,
  timeAgo,
  toneForAction
} from './LiveFeedCard';
import MentionText from '../components/feed/MentionText';
import LikersPopover from '../components/feed/LikersPopover';
import Modal from '../../shared/components/ui/Modal';
import { CreatePost } from '../components/forms/CreatePost';

// Import slick styles (required)
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

// Replies shown under each comment before "Show earlier replies".
const RECENT_REPLIES = 2;
// Comments per page ("Show older comments" loads the next).
const COMMENTS_PAGE = 20;
// Replies loaded at once by "Show earlier replies" (the API's page cap).
const REPLIES_PAGE = 500;
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MAX_COMMENT_FILES = 10;

const isImage = (fileNameOrUrl = '') => /\.(jpe?g|png|gif|webp)$/i.test(fileNameOrUrl);

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const handleFor = (user) => `@${user.name.replace(/\s+/g, '_')}`;

// Adds items by id: our own comment arrives both as the API response and as
// the socket echo, and must show once.
const withUnique = (list, item, atStart = false) =>
  item?.id != null && list.some((existing) => existing?.id === item.id)
    ? list
    : atStart
      ? [item, ...list]
      : [...list, item];

const errorText = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

// Enter sends and Shift+Enter adds a line. With the mention menu open, Enter
// picks the first person and Esc closes the menu.
const Composer = ({
  id,
  value,
  onChange,
  onSend,
  placeholder,
  users,
  onPickUser,
  onCloseMenu,
  autoFocus,
  sending,
  files,
  onAddFiles,
  onRemoveFile
}) => {
  const fileRef = useRef(null);
  const menuOpen = users.length > 0 && value !== '';
  const canSend = (value.trim() || files?.length > 0) && !sending;
  return (
    <div className="rounded-control border border-hairline bg-surface px-3 py-2 focus-within:border-brand-600">
      <div className="relative flex items-end gap-2">
        {menuOpen && (
          <MentionMenu className="left-0 top-full mt-1" users={users} onPick={onPickUser} />
        )}
        <TextareaAutosize
          id={id}
          autoFocus={autoFocus}
          value={value}
          maxLength={2000}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && menuOpen) {
              e.preventDefault();
              onCloseMenu();
            } else if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (menuOpen) onPickUser(users[0]);
              else if (canSend) onSend();
            }
          }}
          className="flex-1 resize-none overflow-hidden border-none bg-transparent text-sm outline-none"
          placeholder={placeholder}
          minRows={1}
        />
        {onAddFiles && (
          <>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt"
              className="hidden"
              onChange={(e) => {
                onAddFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              aria-label="Attach files"
              title="Attach files"
              disabled={sending}
              onClick={() => fileRef.current?.click()}
              className="rounded-action p-1 text-gray-500 hover:bg-surface-sunken hover:text-gray-800 disabled:opacity-50">
              <Paperclip size={17} />
            </button>
          </>
        )}
        <button
          type="button"
          aria-label="Send"
          disabled={!canSend}
          onClick={onSend}
          className="rounded-action p-1 text-brand-700 hover:bg-brand-50 disabled:text-gray-300 disabled:hover:bg-transparent">
          {sending ? <Loader2 size={18} className="animate-spin" /> : <SendHorizontal size={18} />}
        </button>
      </div>
      {files?.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-2">
          {files.map((file) => (
            <li
              key={file.name}
              className="inline-flex max-w-[200px] items-center gap-1.5 rounded-pill bg-surface-sunken py-1 pl-2.5 pr-1 text-xs text-gray-700">
              <Paperclip size={12} className="shrink-0" />
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                disabled={sending}
                onClick={() => onRemoveFile(file.name)}
                className="rounded-full p-0.5 hover:bg-hairline">
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CommentAttachments = ({ attachments }) => {
  if (!Array.isArray(attachments) || attachments.length === 0) return null;
  const images = attachments.filter((a) => isImage(a.name));
  const files = attachments.filter((a) => !isImage(a.name));
  return (
    <div className="mt-2 flex flex-col gap-2">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((image) => (
            <a key={image.id || image.url} href={image.url} target="_blank" rel="noopener noreferrer">
              <img
                src={image.url}
                alt={image.name}
                className="h-24 w-24 rounded-control border border-hairline object-cover"
              />
            </a>
          ))}
        </div>
      )}
      {files.map((file) => (
        <a
          key={file.id || file.url}
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex max-w-full items-center gap-1.5 text-sm text-brand-700 hover:underline">
          <Paperclip size={13} className="shrink-0 text-gray-400" />
          <span className="truncate">{file.name}</span>
        </a>
      ))}
    </div>
  );
};

const SinglePostOverView = () => {
  const userId = Cookies.get('userId');
  const { id } = useParams();
  const location = useLocation();
  const [postData, setPostData] = useState();
  const [mainComment, setMainComment] = useState('');
  const [commentFiles, setCommentFiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentPage, setCommentPage] = useState(0);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentsError, setCommentsError] = useState(false);
  const [loadingRepliesFor, setLoadingRepliesFor] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  const [isPublic, setIsPublic] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [likes, setLikes] = useState(0);
  const { onBack } = useSmartBack({ fallback: '/builder/feed' });
  const [isLiked, setIsLiked] = useState(); // Initialize based on data
  // Tagged users per box: 'main' or a comment id, so each message carries
  // only the people mentioned in it.
  const [tagsByBox, setTagsByBox] = useState({});
  const [userList, setUsersList] = useState([]);
  // Which box the mention menu belongs to: 'main' or a comment id.
  const [mentionTarget, setMentionTarget] = useState(null);
  const [confirmVisibility, setConfirmVisibility] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editDirty, setEditDirty] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [sendingReplyTo, setSendingReplyTo] = useState(null);
  const liking = useRef(false);
  const likingComment = useRef(new Set());
  // Ids of the comments shown, so one arriving twice is added once.
  const commentIds = useRef(new Set());
  const mentionTimer = useRef(null);
  const mentionRequest = useRef(0);
  const { collapsed } = useSidebar();

  const {
    data: SinglePostData,
    refetch: refetchPostData,
    isFetching,
    isError,
    error
  } = useQuery({
    queryKey: ['getPostById', id], // Include `id` to refetch when `id` changes
    queryFn: () => getPostById(id),
    enabled: !!id, // Prevents query from running when `id` is undefined or null
    // Always fetch on open: likes and comments change while you're away.
    staleTime: 0,
    // A missing or hidden post is a 404; retrying won't change that.
    retry: (count, err) => err?.response?.status !== 404 && count < 2
  });
  const notFound =
    (isError && error?.response?.status === 404) ||
    (Array.isArray(SinglePostData) && SinglePostData.length === 0);

  useEffect(() => () => clearTimeout(mentionTimer.current), []);

  // Track page view
  useEffect(() => {
    mixpanel.track('Single Post Over View Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (SinglePostData) {
      setPostData(SinglePostData[0]);
      setIsPublic(SinglePostData[0]?.is_public || false);
      setIsLiked(SinglePostData[0]?.is_liked || false);
      setLikes(SinglePostData[0]?.like_count || 0);
    }
  }, [SinglePostData]);

  const postId = postData?.id;

  // Comments come a page at a time, newest first, each with its latest
  // replies; the rest load on demand.
  const loadComments = useCallback(
    async (page) => {
      if (!postId) return;
      setLoadingComments(true);
      setCommentsError(false);
      try {
        const { comments: list, total } = await getComments(
          postId,
          page,
          COMMENTS_PAGE,
          RECENT_REPLIES
        );
        if (page === 1) commentIds.current = new Set();
        list.forEach((comment) => commentIds.current.add(comment.id));
        setComments((prev) =>
          page === 1 ? list : list.reduce((acc, comment) => withUnique(acc, comment), prev)
        );
        setCommentTotal(total);
        setCommentPage(page);
      } catch (err) {
        console.error('Error loading comments:', err);
        setCommentsError(true);
      } finally {
        setLoadingComments(false);
      }
    },
    [postId]
  );

  useEffect(() => {
    loadComments(1);
  }, [loadComments]);

  // A new comment (ours or from the socket) goes on top and counts once.
  const addComment = useCallback((comment) => {
    if (comment?.id != null) {
      if (commentIds.current.has(comment.id)) return;
      commentIds.current.add(comment.id);
    }
    setComments((prev) => withUnique(prev, { replies: [], reply_count: 0, ...comment }, true));
    setCommentTotal((total) => total + 1);
  }, []);

  const addReply = useCallback((reply) => {
    setComments((prev) =>
      prev.map((comment) => {
        if (comment?.id !== reply.comment_id) return comment;
        const replies = Array.isArray(comment.replies) ? comment.replies : [];
        const next = withUnique(replies, reply);
        return next === replies
          ? comment
          : { ...comment, replies: next, reply_count: (comment.reply_count || replies.length) + 1 };
      })
    );
  }, []);

  // Arrived from a card's Comment button: go straight to the comment box.
  const focusComment = !!location.state?.focusComment;
  useEffect(() => {
    if (!focusComment || !postData) return;
    const box = document.getElementById('post-comment-box');
    box?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    box?.focus({ preventScroll: true });
  }, [focusComment, postData?.id]);

  // Socket listeners for this post
  useEffect(() => {
    const handleComment = (res) => {
      if (res.post_id === postId) addComment(res);
    };
    const handleReply = (res) => {
      if (res.post_id === postId) addReply(res);
    };
    const handleLike = (res) => {
      if (res.post_id === postId) setLikes(res.count);
    };

    socket.on('Comment', handleComment);
    socket.on('Reply', handleReply);
    socket.on('Like', handleLike);

    return () => {
      socket.off('Comment', handleComment);
      socket.off('Reply', handleReply);
      socket.off('Like', handleLike);
    };
  }, [postId, addComment, addReply]);

  const imageAttachments = postData?.attachments?.filter((attach) => isImage(attach.name)) || [];
  const fileAttachments = postData?.attachments?.filter((attach) => !isImage(attach.name)) || [];

  const sliderSettings = {
    dots: true,
    arrows: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1
  };

  const tagIdsFor = (box) => (tagsByBox[box] || []).map((user) => user.id);
  const clearTags = (box) =>
    setTagsByBox((prev) => {
      const next = { ...prev };
      delete next[box];
      return next;
    });

  const addCommentFiles = (fileList) => {
    const picked = Array.from(fileList || []).filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" is over 20MB.`);
        return false;
      }
      if (commentFiles.some((f) => f.name === file.name)) {
        toast.error(`"${file.name}" is already added.`);
        return false;
      }
      return true;
    });
    const room = MAX_COMMENT_FILES - commentFiles.length;
    if (picked.length > room) toast.error(`A comment can have up to ${MAX_COMMENT_FILES} files.`);
    setCommentFiles((prev) => [...prev, ...picked.slice(0, Math.max(0, room))]);
  };

  // Files are uploaded when the comment is sent, so a removed one never
  // reaches S3.
  const uploadCommentFiles = async () => {
    if (commentFiles.length === 0) return [];
    const formData = new FormData();
    commentFiles.forEach((file) => formData.append('files', file));
    const result = await uploadImage(formData);
    const uploaded = result?.data?.files || [];
    return commentFiles.map((file, index) => {
      const match = uploaded.find((f) => f.originalName === file.name) || uploaded[index];
      if (!match?.presignedUrl) throw new Error(`"${file.name}" didn't upload`);
      return { name: file.name, url: match.presignedUrl };
    });
  };

  const handleSendComment = async () => {
    if ((!mainComment.trim() && commentFiles.length === 0) || sendingComment) return;
    setSendingComment(true);
    try {
      const attachments = await uploadCommentFiles();
      const comment = await createComment({
        post_id: postId,
        comment: mainComment,
        attachments,
        tag: tagIdsFor('main')
      });
      addComment(comment);
      setMainComment('');
      setCommentFiles([]);
      setUsersList([]);
      clearTags('main');
      toast.success('Comment added successfully!');
    } catch (err) {
      console.error('Error adding comment:', err);
      toast.error(errorText(err, 'Failed to add comment'));
    } finally {
      setSendingComment(false);
    }
  };

  const handleSendReply = async (commentId) => {
    const replyText = replyTexts[commentId]?.trim();
    if (!replyText || sendingReplyTo !== null) return;
    setSendingReplyTo(commentId);
    try {
      const reply = await createReply({
        reply: replyText,
        comment_id: commentId,
        tag: tagIdsFor(commentId)
      });
      addReply({ ...reply, comment_id: commentId });
      setReplyTexts((prev) => ({ ...prev, [commentId]: '' }));
      setReplyingTo(null);
      setUsersList([]);
      clearTags(commentId);
      toast.success('Reply added successfully!');
    } catch (err) {
      console.error('Error adding reply:', err);
      toast.error(errorText(err, 'Failed to add reply'));
    } finally {
      setSendingReplyTo(null);
    }
  };

  // All of a comment's replies, oldest first, in place of the latest few.
  const showAllReplies = async (commentId) => {
    setLoadingRepliesFor(commentId);
    try {
      const { replies, total } = await getReplies(commentId, 1, REPLIES_PAGE);
      setComments((prev) =>
        prev.map((comment) =>
          comment.id === commentId
            ? { ...comment, replies: [...replies].reverse(), reply_count: total }
            : comment
        )
      );
    } catch (err) {
      toast.error(errorText(err, 'Could not load the replies'));
    } finally {
      setLoadingRepliesFor(null);
    }
  };

  const toggleCommentLike = async (comment) => {
    if (likingComment.current.has(comment.id)) return;
    likingComment.current.add(comment.id);
    try {
      const state = await setCommentLiked(comment.id, !comment.is_liked);
      setComments((prev) =>
        prev.map((c) =>
          c.id === comment.id ? { ...c, like_count: state.like_count, is_liked: state.is_liked } : c
        )
      );
    } catch (err) {
      toast.error(errorText(err, 'Failed to update like status'));
    } finally {
      likingComment.current.delete(comment.id);
    }
  };

  const { mutate: publishPost } = useMutation({
    mutationFn: (post) => editPost(post),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: (_, post) => {
      setIsSubmitting(false);
      setConfirmVisibility(false);
      setIsPublic(post.is_public);
      toast.success(`Post visibility updated to ${post.is_public ? 'Public' : 'Private'}`);
      refetchPostData();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error updating post visibility:', err);
      toast.error(errorText(err, 'Failed to update post visibility'));
    }
  });

  // Only the fields being changed, never the whole post with its relations.
  const handlePublishPost = () => {
    publishPost({ id: postId, is_public: !isPublic });
  };

  const handleLike = async () => {
    if (liking.current) return;
    liking.current = true;
    const payload = { post_id: postId };

    try {
      if (isLiked) {
        await deleteLike(postId);
        setLikes((prev) => Math.max(0, prev - 1));
      } else {
        await createLike(payload);
        setLikes((prev) => prev + 1);
      }
      setIsLiked((prev) => !prev);
    } catch (err) {
      console.error('Error handling like:', err);
      toast.error(errorText(err, 'Failed to update like status'));
    } finally {
      liking.current = false;
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  // Suggestions for the @word being typed: one request per pause in typing,
  // and only the latest request's answer is shown (older ones can land late).
  const searchMentions = (query) => {
    clearTimeout(mentionTimer.current);
    const requestId = ++mentionRequest.current;
    if (!query) {
      setUsersList([]);
      return;
    }
    mentionTimer.current = setTimeout(async () => {
      try {
        const data = await getUsersByChar(query);
        if (requestId !== mentionRequest.current) return;
        setUsersList(
          Array.isArray(data) ? data.map((user) => ({ id: user.id, name: user.name })) : []
        );
      } catch (err) {
        if (requestId !== mentionRequest.current) return;
        console.error('Error fetching users', err);
        setUsersList([]);
      }
    }, 250);
  };

  // Drop tags whose @name was deleted from this box, and look up suggestions
  // for the @word being typed.
  const trackMentions = (text, target) => {
    setMentionTarget(target);
    setTagsByBox((prev) => ({
      ...prev,
      [target]: (prev[target] || []).filter((user) =>
        new RegExp(`${escapeRegExp(handleFor(user))}(\\s|$)`).test(text)
      )
    }));

    const matches = text.match(/@(\w*)$/);
    searchMentions(matches?.[1] || '');
  };

  const insertMention = (text, user, target) => {
    const handle = handleFor(user);
    setTagsByBox((prev) => {
      const tagged = prev[target] || [];
      return tagged.some((t) => t.id === user.id)
        ? prev
        : { ...prev, [target]: [...tagged, user] };
    });
    searchMentions('');
    if (!text || text.includes(handle)) return null;
    return text.replace(/@\w*$/, `${handle} `);
  };

  const closeMentionMenu = () => searchMentions('');

  const tagInfo = postData ? tagInfoFor(postData) : null;
  const TagIcon = tagInfo?.icon;
  const tone = tagInfo ? toneForAction(tagInfo.label) : 'neutral';
  // A visibility toggle bumps updated_at; the post was made at created_at.
  const postedAt = postData?.created_at || postData?.updated_at;
  const isAuthor = isPostAuthor(postData, userId);
  const olderComments = commentTotal - comments.length;

  return (
    <Layout>
      {confirmVisibility && (
        <VisibilityConfirm
          toPublic={!isPublic}
          busy={isSubmitting}
          onConfirm={handlePublishPost}
          onCancel={() => setConfirmVisibility(false)}
        />
      )}
      {editing && postData && (
        <Modal
          isDirty={editDirty}
          confirmOnClose={true}
          name={'Edit Post'}
          width={50}
          onClose={() => setEditing(false)}>
          <CreatePost
            post={postData}
            onSuccess={() => {
              setEditing(false);
              refetchPostData();
            }}
            onClose={() => setEditing(false)}
            onDirtyChange={setEditDirty}
          />
        </Modal>
      )}
      {/* Header */}
      <div
        className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
          collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
        }`}>
        <Header
          data={{ name: 'Post', subtitle: postData?.project_name }}
          backArrow={true}
          onBack={onBack}
        />
      </div>
      <GridWrapper>
        <div className="mx-auto mt-[6rem] flex w-full max-w-[720px] flex-col gap-5 pb-10">
          {!postData && isFetching && (
            <div
              aria-hidden="true"
              className="flex h-80 animate-pulse flex-col gap-4 rounded-panel border border-hairline bg-surface p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-surface-sunken" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-1/3 rounded bg-surface-sunken" />
                  <div className="h-3 w-1/4 rounded bg-surface-sunken" />
                </div>
              </div>
              <div className="h-3 rounded bg-surface-sunken" />
              <div className="h-3 w-5/6 rounded bg-surface-sunken" />
              <div className="h-40 rounded-control bg-surface-sunken" />
              <span className="sr-only" role="status">
                Loading the post…
              </span>
            </div>
          )}
          {!postData && !isFetching && (notFound || isError) && (
            <div className="flex flex-col items-center rounded-panel border border-dashed border-hairline-strong bg-surface px-6 py-14 text-center">
              <p className="text-sm font-semibold text-gray-900">
                {notFound ? 'Post not found' : "Couldn't load this post"}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {notFound
                  ? "It may have been removed, or you don't have access to it."
                  : 'Check your connection and try again.'}
              </p>
              <div className="mt-3 flex gap-4">
                {!notFound && (
                  <button
                    type="button"
                    onClick={() => refetchPostData()}
                    className="text-sm font-medium text-brand-700 hover:underline">
                    Try again
                  </button>
                )}
                <button
                  type="button"
                  onClick={onBack}
                  className="text-sm font-medium text-gray-600 hover:underline">
                  Back to feed
                </button>
              </div>
            </div>
          )}
          {postData && (
            <article
              className={`rounded-panel border border-l-4 border-hairline bg-surface shadow-sm ${TONE_EDGE[tone]}`}>
              <div className="p-6">
                {/* Author */}
                <header className="flex items-start gap-3">
                  <Avatar
                    name={postData.creater_name}
                    src={postData.created_by_dp_url || undefined}
                    size="44"
                    round
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-gray-900">
                      {postData.creater_name}
                    </p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500">
                      {postedAt && (
                        <time dateTime={postedAt} title={timeAgo(postedAt)}>
                          {fullTimestamp(postedAt)}
                        </time>
                      )}
                      {postData.project_name && (
                        <>
                          <span aria-hidden="true">·</span>
                          <span className="inline-flex items-center gap-1">
                            <Building2 size={12} />
                            {postData.project_name}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {isAuthor ? (
                      <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => setConfirmVisibility(true)}
                        title={
                          isPublic
                            ? 'Buyers following this project can see it. Click to make private.'
                            : 'Only your team can see it. Click to make public.'
                        }
                        className={`inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                          isPublic ? TONE_SOFT.info : 'bg-surface-sunken text-gray-600'
                        } hover:opacity-80`}>
                        {isPublic ? <Globe size={13} /> : <Lock size={13} />}
                        {isPublic ? 'Public' : 'Private'}
                      </button>
                    ) : (
                      <span
                        title={
                          isPublic
                            ? 'Buyers following this project can see it.'
                            : 'Only your team can see it.'
                        }
                        className={`inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-medium ${
                          isPublic ? TONE_SOFT.info : 'bg-surface-sunken text-gray-600'
                        }`}>
                        {isPublic ? <Globe size={13} /> : <Lock size={13} />}
                        {isPublic ? 'Public' : 'Private'}
                      </span>
                    )}
                    {isAuthor && (
                      <button
                        type="button"
                        aria-label="Edit post"
                        title="Edit post"
                        onClick={() => setEditing(true)}
                        className="rounded-action p-1.5 text-gray-500 hover:bg-surface-sunken hover:text-gray-800">
                        <Pencil size={16} />
                      </button>
                    )}
                    <button
                      type="button"
                      aria-label="Copy link"
                      title="Copy link"
                      onClick={copyLink}
                      className="rounded-action p-1.5 text-gray-500 hover:bg-surface-sunken hover:text-gray-800">
                      <Link2 size={16} />
                    </button>
                  </div>
                </header>

                {/* Content */}
                <div className="mt-5 flex flex-col gap-4">
                  {tagInfo && (
                    <span
                      className={`inline-flex w-fit items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-medium ${TONE_SOFT[tone]}`}>
                      <TagIcon size={13} />
                      {tagInfo.label}
                    </span>
                  )}

                  {postData.post && (
                    <p className="whitespace-pre-line break-words text-base leading-relaxed text-gray-800">
                      <MentionText text={postData.post} />
                    </p>
                  )}

                  <TaggedPeople post={postData} max={10} />

                  {imageAttachments.length > 0 && (
                    <div className="relative overflow-hidden rounded-control bg-gray-900">
                      {imageAttachments.length === 1 ? (
                        <img
                          src={imageAttachments[0].url}
                          alt={imageAttachments[0].name}
                          className="max-h-[480px] w-full object-contain"
                        />
                      ) : (
                        <Slider {...sliderSettings}>
                          {imageAttachments.map((attach) => (
                            <div key={attach.id} className="flex items-center justify-center">
                              <img
                                src={attach.url}
                                alt={attach.name}
                                className="max-h-[480px] w-full object-contain"
                              />
                            </div>
                          ))}
                        </Slider>
                      )}
                    </div>
                  )}

                  {fileAttachments.length > 0 && (
                    <ul className="flex flex-col gap-2">
                      {fileAttachments.map((file) => (
                        <li
                          key={file.id}
                          className="flex items-center gap-3 rounded-control border border-hairline bg-surface-muted px-3 py-2">
                          <Paperclip size={15} className="shrink-0 text-gray-400" />
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="min-w-0 flex-1 truncate text-sm text-brand-700 hover:underline">
                            {file.name}
                          </a>
                          <a
                            href={file.url}
                            download={file.name}
                            aria-label={`Download ${file.name}`}
                            className="rounded-action p-1 text-gray-500 hover:bg-surface-sunken hover:text-gray-800">
                            <Download size={15} />
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Actions. The like count opens who liked it. */}
              <div className="grid grid-cols-2 gap-1 border-t border-hairline px-3 py-1.5">
                <div className="flex items-center justify-center rounded-control transition-colors hover:bg-surface-sunken">
                  <button
                    type="button"
                    aria-pressed={!!isLiked}
                    onClick={handleLike}
                    className={`flex items-center gap-2 py-2 pl-2 text-sm font-medium ${
                      likes > 0 ? 'pr-1' : 'pr-2'
                    } ${isLiked ? 'text-brand-700' : 'text-gray-600'}`}>
                    <ThumbsUp size={17} fill={isLiked ? 'currentColor' : 'none'} />
                    Like
                  </button>
                  <LikersPopover
                    postId={postId}
                    count={likes}
                    label={`· ${likes}`}
                    buttonClassName="py-2 pr-2 text-sm text-gray-500 hover:text-brand-700 hover:underline"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => document.getElementById('post-comment-box')?.focus()}
                  className="flex items-center justify-center gap-2 rounded-control py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-surface-sunken">
                  <MessageSquare size={17} />
                  Comment
                  {commentTotal > 0 && <span className="text-gray-500">· {commentTotal}</span>}
                </button>
              </div>
            </article>
          )}

          {/* Discussion */}
          {postData && (
            <section className="rounded-panel border border-hairline bg-surface p-6 shadow-sm">
              <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                Comments
                {commentTotal > 0 && (
                  <span className="rounded-pill bg-surface-sunken px-2 py-0.5 text-xs font-medium text-gray-600">
                    {commentTotal}
                  </span>
                )}
              </h2>

              <div className="mt-4">
                <Composer
                  id="post-comment-box"
                  value={mainComment}
                  onChange={(text) => {
                    setMainComment(text);
                    trackMentions(text, 'main');
                  }}
                  onSend={handleSendComment}
                  sending={sendingComment}
                  placeholder="Write a comment… use @ to mention someone"
                  users={mentionTarget === 'main' ? userList : []}
                  onCloseMenu={closeMentionMenu}
                  onPickUser={(u) => {
                    const next = insertMention(mainComment, u, 'main');
                    if (next !== null) setMainComment(next);
                  }}
                  files={commentFiles}
                  onAddFiles={addCommentFiles}
                  onRemoveFile={(name) =>
                    setCommentFiles((prev) => prev.filter((f) => f.name !== name))
                  }
                />
              </div>

              {comments.length === 0 ? (
                <p className="mt-6 text-center text-sm text-gray-500">
                  {loadingComments
                    ? 'Loading comments…'
                    : commentsError
                      ? "Couldn't load the comments."
                      : 'No comments yet. Start the conversation.'}
                </p>
              ) : (
                <ul className="mt-6 flex flex-col gap-5">
                  {comments.map((comment) => {
                    const replies = Array.isArray(comment?.replies) ? comment.replies : [];
                    const hidden = Math.max(0, (comment.reply_count || replies.length) - replies.length);

                    return (
                      <li key={comment.id} className="flex items-start gap-3">
                        <Avatar
                          name={comment?.creater_name}
                          src={comment?.user?.user_pic?.url}
                          size="34"
                          round
                        />
                        <div className="min-w-0 flex-1">
                          <div className="rounded-control bg-surface-muted px-3.5 py-2.5">
                            <p className="text-sm font-semibold text-gray-900">
                              {comment?.creater_name}
                            </p>
                            {comment?.comment && (
                              <p className="mt-0.5 whitespace-pre-line break-words text-sm text-gray-800">
                                <MentionText text={comment.comment} />
                              </p>
                            )}
                            <CommentAttachments attachments={comment?.attachments} />
                          </div>
                          <div className="mt-1 flex items-center gap-3 px-1 text-xs text-gray-500">
                            {comment?.created_at && (
                              <time
                                dateTime={comment.created_at}
                                title={fullTimestamp(comment.created_at)}>
                                {timeAgo(comment.created_at)}
                              </time>
                            )}
                            <button
                              type="button"
                              aria-pressed={!!comment.is_liked}
                              onClick={() => toggleCommentLike(comment)}
                              className={`inline-flex items-center gap-1 font-medium hover:text-brand-700 ${
                                comment.is_liked ? 'text-brand-700' : 'text-gray-600'
                              }`}>
                              <ThumbsUp size={12} fill={comment.is_liked ? 'currentColor' : 'none'} />
                              Like
                              {comment.like_count > 0 && <span>· {comment.like_count}</span>}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setReplyingTo((current) =>
                                  current === comment.id ? null : comment.id
                                )
                              }
                              className="font-medium text-gray-600 hover:text-brand-700">
                              Reply
                            </button>
                          </div>

                          {/* Replies */}
                          {(replies.length > 0 || hidden > 0) && (
                            <div className="mt-3 flex flex-col gap-3 border-l-2 border-hairline pl-4">
                              {hidden > 0 && (
                                <button
                                  type="button"
                                  disabled={loadingRepliesFor === comment.id}
                                  onClick={() => showAllReplies(comment.id)}
                                  className="w-fit text-xs font-medium text-brand-700 hover:underline disabled:opacity-60">
                                  {loadingRepliesFor === comment.id
                                    ? 'Loading replies…'
                                    : `Show ${hidden} earlier ${hidden === 1 ? 'reply' : 'replies'}`}
                                </button>
                              )}
                              {replies.map((reply) => (
                                <div key={reply.id} className="flex items-start gap-2.5">
                                  <Avatar name={reply.creater_name} size="28" round />
                                  <div className="min-w-0 flex-1">
                                    <div className="rounded-control bg-surface-muted px-3 py-2">
                                      <p className="text-sm font-semibold text-gray-900">
                                        {reply.creater_name}
                                      </p>
                                      <p className="mt-0.5 whitespace-pre-line break-words text-sm text-gray-800">
                                        <MentionText text={reply?.reply} />
                                      </p>
                                    </div>
                                    {reply?.created_at && (
                                      <time
                                        dateTime={reply.created_at}
                                        title={fullTimestamp(reply.created_at)}
                                        className="mt-1 block px-1 text-xs text-gray-500">
                                        {timeAgo(reply.created_at)}
                                      </time>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {replyingTo === comment.id && (
                            <div className="mt-3">
                              <Composer
                                autoFocus
                                value={replyTexts[comment.id] || ''}
                                onChange={(text) => {
                                  setReplyTexts((prev) => ({ ...prev, [comment.id]: text }));
                                  trackMentions(text, comment.id);
                                }}
                                onSend={() => handleSendReply(comment.id)}
                                sending={sendingReplyTo === comment.id}
                                placeholder={`Reply to ${comment?.creater_name || 'comment'}…`}
                                users={mentionTarget === comment.id ? userList : []}
                                onCloseMenu={closeMentionMenu}
                                onPickUser={(u) => {
                                  const next = insertMention(replyTexts[comment.id], u, comment.id);
                                  if (next !== null) {
                                    setReplyTexts((prev) => ({ ...prev, [comment.id]: next }));
                                  }
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {olderComments > 0 && (
                <div className="mt-5 text-center">
                  <button
                    type="button"
                    disabled={loadingComments}
                    onClick={() => loadComments(commentPage + 1)}
                    className="text-sm font-medium text-brand-700 hover:underline disabled:opacity-60">
                    {loadingComments
                      ? 'Loading…'
                      : `Show ${olderComments} older ${olderComments === 1 ? 'comment' : 'comments'}`}
                  </button>
                </div>
              )}
              {commentsError && comments.length > 0 && (
                <p className="mt-4 text-center text-sm text-gray-500">
                  Couldn&apos;t load more comments.{' '}
                  <button
                    type="button"
                    onClick={() => loadComments(commentPage + 1)}
                    className="font-medium text-brand-700 hover:underline">
                    Try again
                  </button>
                </p>
              )}
            </section>
          )}
        </div>
      </GridWrapper>
    </Layout>
  );
};

export default SinglePostOverView;
